/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 *
 * @author Evgenij Tyan
 * @author Yakov Gushchin
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Evgeniy Naumov
 * @version 1.22.0
 * @since 1.0.0
 */
import { FC, useEffect } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { IRootReducer } from '@/main/reducers/rootReducer';
import { IOptionItemProps } from '@components/common/Select/Select';
import { makeSelectTranslationCommon } from '@modules/internationalization/intSelectors';
import { ICommonTranslation } from '@modules/internationalization/types';
import { logBusinessEvent } from '@modules/logging/loggingActions';
import { ELogBusinessEventName, ELogBusinessEventType } from '@modules/logging/loggingConstants';
import { ILogBusinessEventPayload } from '@modules/logging/loggingTypes';

import {
    modalInquiryAttachFile,
    modalInquiryClose,
    modalInquiryCreate,
    modalInquiryDetachFile,
    modalInquiryLoadTopic,
    modalInquiryLoadUser,
} from '../createInquiryActions';
import {
    makeSelectorInquiryAttachList,
    makeSelectorInquiryIsLoading,
    makeSelectorInquiryTopicInfo,
    makeSelectorInquiryTopicList,
    makeSelectorInquiryUserInfo,
} from '../createInquirySelectors';
import { getResultValue } from '../createInquiryUtils';
import {
    IModalInquiryAttachFileActionPayload,
    IModalInquiryCreateActionPayload,
    IModalInquiryLoadTopicPayload,
    IModalInquiryLoadUserPayload,
} from '../types/createInquiryActionTypes';
import {
    ETopicAttrVisibility,
    ETopicType,
    IInquiryAttachProps,
    IInquiryUser,
    ITopicCustomerAttr,
    ITopicInfo,
} from '../types/createInquiryTypes';
import CreateInquiryForm, { IFormValues } from './CreateInquiryForm/CreateInquiryForm';
import {
    CreateInquiryClientStyled,
    CreateInquiryContainerStyled,
    CreateInquiryHeaderStyled,
    CreateInquiryTitleStyled,
} from './styled/CreateInquiryStyled';

export interface IConnectedProps {
    attachList: IInquiryAttachProps[];
    isLoading: boolean;
    topicInfo: ITopicInfo;
    topicList: IOptionItemProps[];
    translation: ICommonTranslation;
    userInfo: IInquiryUser;
}

export interface IDispatchProps {
    attachFile: (payload: IModalInquiryAttachFileActionPayload) => void;
    closeInquiry: () => void;
    createInquiry: (payload: IModalInquiryCreateActionPayload) => void;
    dettachFile: (payload: number) => void;
    loadInquiryUser: (payload: IModalInquiryLoadUserPayload) => void;
    modalInquiryLoadTopic: (payload: IModalInquiryLoadTopicPayload) => void;
    logBusinessEvent: (payload: ILogBusinessEventPayload) => void;
}

export interface ICreateInquiryProps {
    onCloseModal: () => void;
    type: ETopicType;
    userId: string;
}

type TCreateInquiry = ICreateInquiryProps & IDispatchProps & IConnectedProps;

const CreateInquiry: FC<TCreateInquiry> = (props) => {
    const { createInquiry: inquiryTranslation } = props.translation;

    useEffect(() => {
        props.logBusinessEvent({
            eventName: ELogBusinessEventName.B2B_SHOW_INQUIRY_MASTER,
            type: ELogBusinessEventType.common,
        });
        props.loadInquiryUser({
            type: props.type,
            userId: props.userId,
        });
        return (): void => {
            props.closeInquiry();
        };
    }, []);

    const selectTopicHandler = (id: string): void => {
        if (id) {
            props.modalInquiryLoadTopic({
                clientId: parseInt(props.userInfo.clntId),
                subscriberId: parseInt(props.userInfo.subsId),
                topicId: id,
            });
        }
    };
    const onCreate = (values: IFormValues, attrs: ITopicCustomerAttr[]): void => {
        const result = {
            contact: {
                customer: {
                    id: props.userInfo?.clntId ?? '',
                },
                subscriber: {
                    id: props.userInfo?.subsId ?? '',
                },
            },
            inquiry: {
                attributes: attrs
                    .filter(
                        (attr: ITopicCustomerAttr) => attr.declaration.visibility !== ETopicAttrVisibility.READ_ONLY,
                    )
                    .map((attr: ITopicCustomerAttr) => {
                        return {
                            declaration: {
                                dataType: attr.declaration.dataType,
                            },
                            id: attr.id,
                            valueList: [getResultValue(attr, values)],
                        };
                    }),
                description: values.description ?? '',
                email: values?.email ?? '',
                phone: values?.phone ?? '',
                subscriber: {
                    id: props.userInfo?.subsId ?? '',
                },
                topic: {
                    id: values?.topic ?? '',
                },
            },
            type: props.type,
        };
        props.createInquiry({
            callback: props.onCloseModal,
            params: result,
        });
    };
    const client = props.type === ETopicType.account ? props.userInfo.account : props.userInfo.msisdn;
    const clientName = props.userInfo.name ? '- ' + props.userInfo.name : '';
    return (
        <CreateInquiryContainerStyled data-component={'modal-popup'} data-name={'create-inquiry'}>
            <CreateInquiryHeaderStyled>
                <CreateInquiryTitleStyled>{inquiryTranslation.createTitle}</CreateInquiryTitleStyled>
                <CreateInquiryClientStyled>
                    {inquiryTranslation.client} {client} {clientName}
                </CreateInquiryClientStyled>
            </CreateInquiryHeaderStyled>

            <CreateInquiryForm
                attachFile={props.attachFile}
                attachList={props.attachList}
                closeModal={props.onCloseModal}
                dettachFile={props.dettachFile}
                isLoading={props.isLoading}
                topicInfo={props.topicInfo}
                topicList={props.topicList}
                translation={props.translation}
                type={props.type}
                userInfo={props.userInfo}
                onCreate={onCreate}
                onSelectTopic={selectTopicHandler}
            />
        </CreateInquiryContainerStyled>
    );
};

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    attachList: makeSelectorInquiryAttachList(),
    isLoading: makeSelectorInquiryIsLoading(),
    topicInfo: makeSelectorInquiryTopicInfo(),
    topicList: makeSelectorInquiryTopicList(),
    translation: makeSelectTranslationCommon(),
    userInfo: makeSelectorInquiryUserInfo(),
});

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    attachFile: (payload: IModalInquiryAttachFileActionPayload) => dispatch(modalInquiryAttachFile(payload)),
    closeInquiry: () => dispatch(modalInquiryClose()),
    createInquiry: (payload: IModalInquiryCreateActionPayload) => dispatch(modalInquiryCreate(payload)),
    dettachFile: (payload: number) => dispatch(modalInquiryDetachFile(payload)),
    loadInquiryUser: (payload: IModalInquiryLoadUserPayload) => dispatch(modalInquiryLoadUser(payload)),
    modalInquiryLoadTopic: (payload: IModalInquiryLoadTopicPayload) => dispatch(modalInquiryLoadTopic(payload)),
    logBusinessEvent: (payload) => dispatch(logBusinessEvent(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CreateInquiry);
